import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import api, { API } from "@/lib/api";
import { dateBadge, sportName, parseDate } from "@/lib/helpers";
import { SportIcon } from "@/components/SportIcon";
import { ChevronLeft, ChevronRight, CalendarDays, List, CalendarClock, Loader2, CalendarPlus, MapPin } from "lucide-react";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const SPORTS = [["", "All Sports"], ["running", "Running"], ["trail-running", "Trail Running"], ["cycling", "Cycling"], ["triathlon", "Triathlon"], ["duathlon", "Duathlon"], ["fitness-racing", "Fitness Racing"]];

export default function CalendarPage() {
  const [races, setRaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("month");
  const [sport, setSport] = useState("");
  const [cursor, setCursor] = useState(() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1); });

  useEffect(() => {
    setLoading(true);
    const qs = sport ? `?sport=${sport}` : "";
    api.get(`/races${qs}`).then(({ data }) => setRaces(data.races)).finally(() => setLoading(false));
  }, [sport]);

  const byDate = {};
  races.forEach((r) => { const d = parseDate(r.date); if (d) { const k = d.toISOString().slice(0, 10); (byDate[k] = byDate[k] || []).push(r); } });
  const sorted = [...races].sort((a, b) => a.date.localeCompare(b.date));
  const upcoming = sorted.filter((r) => parseDate(r.date) >= new Date(new Date().toDateString()));

  const renderMonth = () => {
    const year = cursor.getFullYear(), month = cursor.getMonth();
    const startDay = (new Date(year, month, 1).getDay() + 6) % 7;
    const days = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startDay; i++) cells.push(null);
    for (let d = 1; d <= days; d++) cells.push(d);
    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold uppercase text-xl text-slate-800">{MONTHS[month]} {year}</h3>
          <div className="flex gap-2">
            <button onClick={() => setCursor(new Date(year, month - 1, 1))} className="p-2 border border-slate-300 rounded-lg hover:bg-slate-100"><ChevronLeft size={16} /></button>
            <button onClick={() => setCursor(new Date(year, month + 1, 1))} className="p-2 border border-slate-300 rounded-lg hover:bg-slate-100"><ChevronRight size={16} /></button>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center text-xs font-semibold text-slate-400 mb-2">{["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d}>{d}</div>)}</div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((d, i) => {
            if (!d) return <div key={i} className="aspect-square" />;
            const k = new Date(year, month, d).toISOString().slice(0, 10);
            const evs = byDate[k] || [];
            return (
              <div key={i} className={`aspect-square rounded-lg border p-1 text-sm relative overflow-hidden ${evs.length ? "border-[#22d3ee] bg-cyan-50" : "border-slate-100"}`}>
                <span className={evs.length ? "font-bold text-[#0a1420]" : "text-slate-500"}>{d}</span>
                {evs.slice(0, 2).map((e) => <Link key={e.id} to={`/race/${e.slug}`} className="block truncate text-[9px] mt-0.5 text-[#06b6d4] font-medium hover:underline">{e.name}</Link>)}
                {evs.length > 2 && <span className="text-[9px] text-slate-400">+{evs.length - 2}</span>}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const List_ = ({ items }) => (
    <div className="space-y-3">
      {items.length === 0 && <p className="text-slate-500 py-8 text-center">No races found. <Link to="/races" className="text-[#06b6d4] font-semibold">Find races</Link></p>}
      {items.map((r) => { const b = dateBadge(r.date); return (
        <div key={r.id} className="flex items-center gap-4 bg-white border border-slate-200 rounded-xl p-4">
          <div className="bg-[#0a1420] text-white rounded-lg px-3 py-2 text-center leading-tight shrink-0">
            <div className="font-display font-black text-xl text-[#22d3ee]">{b.day}</div><div className="font-display font-bold text-[10px] tracking-widest">{b.month}</div>
          </div>
          <div className="flex-1 min-w-0">
            <Link to={`/race/${r.slug}`} className="font-display font-bold uppercase text-slate-800 hover:text-[#06b6d4] truncate flex items-center gap-2">{r.name}{r.is_demo && <span className="text-[9px] font-bold bg-[#22d3ee] text-[#0a1420] px-1.5 py-0.5 rounded shrink-0">DEMO</span>}</Link>
            <div className="flex items-center gap-2 text-sm text-slate-500"><SportIcon sport={r.sport} size={13} />{sportName(r.sport)} · <MapPin size={12} />{r.city}</div>
          </div>
          <a href={`${API}/races/${r.id}/ics`} className="hidden sm:flex items-center gap-1 text-sm text-[#06b6d4] font-semibold hover:underline"><CalendarPlus size={15} /> Add</a>
        </div>
      ); })}
    </div>
  );

  return (
    <Layout>
      <div className="bg-[#0a1420] py-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-display font-black uppercase text-4xl text-white flex items-center gap-3"><CalendarDays className="text-[#22d3ee]" /> Race Calendar</h1>
          <p className="text-slate-300 mt-1">Browse upcoming races across Malaysia &amp; Singapore by month, sport and location.</p>
          <div className="flex flex-wrap items-center gap-3 mt-5">
            <div className="flex bg-navy-card rounded-lg p-1">
              {[["month", CalendarDays, "Month"], ["list", List, "List"], ["upcoming", CalendarClock, "Upcoming"]].map(([v, Icon, label]) => (
                <button key={v} onClick={() => setView(v)} data-testid={`calendar-view-${v}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold ${view === v ? "bg-[#22d3ee] text-[#0a1420]" : "text-slate-300"}`}><Icon size={16} />{label}</button>
              ))}
            </div>
            <select value={sport} onChange={(e) => setSport(e.target.value)} data-testid="calendar-sport-filter" className="bg-navy-card text-white border border-slate-700 rounded-lg px-3 py-2 text-sm outline-none">
              {SPORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {loading ? <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-[#22d3ee]" size={32} /></div> :
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            {view === "month" && renderMonth()}
            {view === "list" && <List_ items={sorted} />}
            {view === "upcoming" && <List_ items={upcoming} />}
          </div>}
      </div>
    </Layout>
  );
}
